import React, { useEffect, useMemo, useState } from "react";
import "react-circular-progressbar/dist/styles.css";
import { c, t } from "ttag";
import { useStoreActions, useStoreState } from "../../models/RootStore";
import PowerCircle from "./PowerCircle";
import PowerConsumptionGraph from "./PowerConsumptionGraph";
import SiteCountCard from "./SiteCountCard";
import useCoolPoll from "@hooks/useCoolPoll";
import { makeStyles } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'grid',
    gridTemplateColumns: '3fr 3fr 4fr',
    gap: '10px',
    width: '100%',
    height: 215,
  },
}));

const OperationalOverview: React.FC<any> = (props) => {
  const classes = useStyles();

  const [loading, setLoading] = useState<{ siteCountCard: boolean; powerCircle: boolean }>({ siteCountCard: false, powerCircle: false });

  const { selections } = useStoreState((s) => s.selections);
  const  allSystems  = useStoreState((s) => s.systems.allSystems);
  const getSiteSystemsPowerStats = useStoreActions((actions) => actions.sites.getSiteSystemsPowerStats);
  const getSiteSystemsStats = useStoreActions((actions) => actions.sites.getSiteSystemsStats);
  const getCustomerSystemsPowerStats = useStoreActions((actions) => actions.customers.getCustomerSystemsPowerStats);
  const [power, setPower] = useState<any>({ realMaxPower: 0, max: 0, actual: 0, systemsNumber: 0, includedSystems: 0 });
  const [systemsPower, setSystemsPower] = useState<any>({});
  const getSiteOnUnitsStats = useStoreActions((actions) => actions.sites.getSiteOnUnitsStats);
  const getCustomerOnUnitsStats = useStoreActions((actions) => actions.customers.getCustomerOnUnitsStats);
  const getCustomerSystemsStats = useStoreActions((actions) => actions.customers.getCustomerSystemsStats);
  const [unitStats, setUnitStats] = useState<any>({
      totalUnits: 0,
      onUnits: 0,
      offUnits: 0,
      onUnitsCool: 0,
      onUnitsHeat: 0,
      onUnitsOther: 0
  });
  const { realMax, max, actual, systemsNumber, includedSystems } = power;
  const { customerId, siteId } = selections;

  const totalSystems = useMemo(() => {
    if (siteId) {
      return Object.values(allSystems).filter((system: any) => system.site === siteId);
    }
    return Object.values(allSystems);
  }, [allSystems, siteId]);

  const getTotalSystemCount = (systemsStatsRes: any[]) => {
    if (!systemsStatsRes || systemsStatsRes.length === 0) {
      return 0;
    }
    if (Object.prototype.hasOwnProperty.call(systemsStatsRes[0], 'systemCount')) {
      // Customer data: sum up systemCount from all sites
      return systemsStatsRes.reduce(
        (total, site) => total + site.systemCount,
        0
      );
    }
    return systemsStatsRes.length;
  };

  const fetchUnitStats = async () => {
    setLoading(old => ({ ...old, siteCountCard: true }));
    try {
      let unitStatsRes;
      if (siteId) {
        unitStatsRes = await getSiteOnUnitsStats(siteId);
      } else if (customerId) {
        unitStatsRes = await getCustomerOnUnitsStats(customerId);
      } else {
        return;
      }
      setUnitStats(unitStatsRes);
    } catch (error) {
      console.error('Error fetching unit stats:', error);
    } finally {
      setLoading(old => ({ ...old, siteCountCard: false }));
    }
  };

  const fetchData = async () => {

    setLoading(old => ({ ...old, powerCircle: true }));

    try {
      let powerRes;
      if (siteId) {
        powerRes = await getSiteSystemsPowerStats(siteId);
      } else if (customerId) {
        powerRes = await getCustomerSystemsPowerStats(customerId);
      } else {
        return;
      }

      const { totals, systemsData } = powerRes;
      const {
        totalRealMaxPowerConsumption,
        totalMaxPowerConsumption,
        totalLastPowerConsumption,
      } = totals;
      const formattedData = systemsData.map(
        ({
          totalRealMaxPowerConsumption,
          totalMaxPowerConsumption,
          totalLastPowerConsumption,
          name,
        }: any) => ({
          realMax: totalRealMaxPowerConsumption,
          max: totalMaxPowerConsumption,
          actual: totalLastPowerConsumption,
          systemName: name,
        })
      );
      setSystemsPower(formattedData);
      setPower({
        realMax: totalRealMaxPowerConsumption,
        max: totalMaxPowerConsumption,
        actual: totalLastPowerConsumption,
        systemsNumber: getTotalSystemCount(totalSystems),
        includedSystems: systemsData?.length ?? 0,
      });
    } catch (error) {
      console.error('Error fetching power state data:', error);
    } finally {
      setLoading(old => ({ ...old, powerCircle: false }));
    }
  };

  useCoolPoll({
    frequency: 60000,
    onPoll: () => {
      fetchData();
    }
  })

  useEffect(() => {
    fetchData();
    fetchUnitStats();
  }, [siteId, customerId]);

  return (
    <>
      <div className={classes.container}>
        <SiteCountCard
          totalIndoorUnits={unitStats.totalUnits}
          conditionIndoorUnits={unitStats.onUnits}
          blueIndoorUnits={unitStats.onUnitsCool}
          redIndoorUnits={unitStats.onUnitsHeat}
          title={t`Indoor Units ON`}
          customerId={customerId}
          siteId={siteId}
          refresh={fetchUnitStats}
          loading={loading.siteCountCard}
        />
        <PowerCircle
          maxPower={max}
          realMaxPower={realMax}
          actualPower={actual}
          systemsNumber={systemsNumber}
          includedSystems={includedSystems}
          title={t`Power (estimated)`}
          systemsPower={systemsPower}
          loading={loading.powerCircle}
        />
        <PowerConsumptionGraph />
      </div>
    </>
  );
};

export default OperationalOverview;
